/**
 * 🏷️ SIMPLE VERIFICATION BADGE - Simplified status for User Directory
 * Shows high-level verification status without percentages or detailed progress
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Error,
  Assignment,
  HourglassEmpty,
} from '@mui/icons-material';

export type SimpleVerificationStatus =
  | 'not_started'
  | 'in_progress'
  | 'pending_review'
  | 'verified'
  | 'rejected';

interface SimpleVerificationBadgeProps {
  status: SimpleVerificationStatus;
  size?: 'small' | 'medium';
}

export const SimpleVerificationBadge: React.FC<SimpleVerificationBadgeProps> = ({
  status,
  size = 'small'
}) => {
  const getStatusConfig = (status: SimpleVerificationStatus) => {
    const configs = {
      'not_started': {
        label: 'Not Started',
        color: 'default' as const,
        icon: <Assignment />,
        tooltip: 'User has not started verification process'
      },
      'in_progress': {
        label: 'In Progress',
        color: 'info' as const,
        icon: <HourglassEmpty />,
        tooltip: 'User is currently completing verification steps'
      },
      'pending_review': {
        label: 'Pending Review',
        color: 'warning' as const,
        icon: <Schedule />,
        tooltip: 'Verification submitted, awaiting admin approval'
      },
      'verified': {
        label: 'Verified',
        color: 'success' as const,
        icon: <CheckCircle />,
        tooltip: 'User is fully verified'
      },
      'rejected': {
        label: 'Rejected',
        color: 'error' as const,
        icon: <Error />,
        tooltip: 'Verification was rejected'
      },
    };

    return configs[status] || configs['not_started'];
  };

  const config = getStatusConfig(status);

  return (
    <Tooltip title={config.tooltip} arrow>
      <Chip
        icon={config.icon}
        label={config.label}
        size={size}
        color={config.color}
        variant={status === 'verified' ? 'filled' : 'outlined'}
        sx={{
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? 14 : 16
          },
          ...(status === 'verified' ? {
            fontWeight: 600
          } : {})
        }}
      />
    </Tooltip>
  );
};

/**
 * Convert detailed verification status to simple status for User Directory
 */
export const getSimpleVerificationStatus = (user: any): SimpleVerificationStatus => {
  // Check driver verification first
  if (user.driverVerification) {
    const driverStatus = user.driverVerification.status;
    const percentage = user.driverVerification.completionPercentage || 0;

    if (driverStatus === 'approved' && percentage === 100) {
      return 'verified';
    }
    if (driverStatus === 'pending_review') {
      return 'pending_review';
    }
    if (driverStatus === 'rejected') {
      return 'rejected';
    }
    if (driverStatus === 'draft' && percentage > 0) {
      return 'in_progress';
    }
    if (percentage === 0) {
      return 'not_started';
    }
  }

  // Check verification_stage
  if (user.verification_stage || user.verificationStage) {
    const stage = user.verification_stage || user.verificationStage;

    switch (stage) {
      case 'not_started':
        return 'not_started';

      case 'nin_completed':
      case 'documents_uploaded':
        // These are in-progress states
        return 'in_progress';

      case 'completed':
        return 'verified';

      default:
        break;
    }
  }

  // Fallback to is_verified
  if (user.isVerified || user.is_verified) {
    return 'verified';
  }

  // Check identity_verified (means they started)
  if (user.identity_verified || user.identityVerified) {
    return 'in_progress';
  }

  // Default
  return 'not_started';
};
